import PageTitle from '@/components/PageTitle';
import ThemeWrapper from '@/components/ThemeWrapper';
import ClassAccessDeniedMessage from '@/components/course/ClassAccessDeniedMessage';
import useThemeColors from '@/hooks/useThemeColors';
import ClassLayout from '@/layouts/ClassLayout';
import { ClassData, CourseData, NextTopicData, SectionData, TopicData } from '@/types';
import { usePage } from '@inertiajs/react';
import { ChevronLeft, ChevronRight, List, X } from 'lucide-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ClassInfo from './ClassDetail/Partials/ClassInfo';
import CourseContentSidebar from './ClassDetail/Partials/CourseContentSidebar';
import VideoPlayer from './ClassDetail/Partials/VideoPlayer';

type Props = {
    course: CourseData;
    section?: SectionData;
    topic?: TopicData;
    class: ClassData;
    is_class_completed?: boolean;
    next_topic?: NextTopicData | null;
    can_access_class?: boolean;
    access_denial_reason?: 'no_enrollments' | 'not_enrolled_in_course';
    user_has_any_enrollment?: boolean;
};

const ClassDetailContent: React.FC = () => {
    const { t } = useTranslation();
    const colors = useThemeColors();
    const { props } = usePage<Props>();
    const {
        course,
        topic,
        class: classData,
        is_class_completed,
        next_topic,
        can_access_class = true,
        access_denial_reason = 'no_enrollments',
    } = props;
    const [isCompleted, setIsCompleted] = useState(is_class_completed || false);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [isDesktopSidebarCollapsed, setIsDesktopSidebarCollapsed] = useState(false);

    return (
        <ClassLayout
            courseTitle={course.title}
            courseSlug={course.slug}
            backUrl={topic ? `/courses/${course.slug}/topics/${topic.id}` : undefined}
        >
            <PageTitle title={classData.title} />
            <div className="relative flex h-[calc(100vh-57px)]">
                {/* Main Content Area */}
                <div className="flex flex-1 flex-col overflow-y-auto">
                    {/* Video Player or Access Denied */}
                    {can_access_class ? (
                        <VideoPlayer
                            key={`video-${classData.id}`}
                            videoUrl={classData.video_url}
                            vimeoUrl={classData.vimeo_url}
                        />
                    ) : (
                        <ClassAccessDeniedMessage accessDenialReason={access_denial_reason} />
                    )}

                    {/* Class Info and Controls */}
                    <ClassInfo
                        key={`class-info-${classData.id}`}
                        classData={classData}
                        onCompletionChange={setIsCompleted}
                        course={course}
                        topic={topic}
                        isCompleted={isCompleted}
                        nextTopic={next_topic}
                        canAccessClass={can_access_class}
                    />
                </div>

                {/* Mobile Sidebar Toggle Button */}
                <button
                    onClick={() => setIsSidebarOpen(true)}
                    className="btn-gradient fixed right-6 bottom-6 z-40 flex h-14 w-14 items-center justify-center rounded-full lg:hidden"
                    aria-label={t('course.showCourseContent')}
                >
                    <List size={24} />
                </button>

                {/* Mobile Sidebar Overlay */}
                {isSidebarOpen && (
                    <div
                        className="fixed inset-0 z-40 bg-black/50 lg:hidden"
                        onClick={() => setIsSidebarOpen(false)}
                    />
                )}

                {/* Desktop Sidebar Toggle Button - Always visible on edge */}
                <button
                    onClick={() => setIsDesktopSidebarCollapsed(!isDesktopSidebarCollapsed)}
                    className="absolute top-1/2 z-30 hidden h-12 w-6 -translate-y-1/2 items-center justify-center rounded-l-md border border-r-0 border-gray-200 bg-white text-gray-500 shadow-sm transition-all hover:bg-gray-50 hover:text-gray-700 lg:flex"
                    style={{ right: isDesktopSidebarCollapsed ? 0 : '320px' }}
                    aria-label={isDesktopSidebarCollapsed ? t('course.showCourseContent') : t('course.hideCourseContent')}
                >
                    {isDesktopSidebarCollapsed ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
                </button>

                {/* Sidebar - Course Content */}
                <div
                    className={`fixed top-0 right-0 z-50 h-full w-80 transform transition-all duration-300 lg:relative lg:z-auto ${
                        isSidebarOpen ? 'translate-x-0' : 'translate-x-full lg:translate-x-0'
                    } ${isDesktopSidebarCollapsed ? 'lg:w-0 lg:overflow-hidden' : 'lg:w-80'}`}
                >
                    {/* Mobile Close Button */}
                    <button
                        onClick={() => setIsSidebarOpen(false)}
                        className="absolute top-4 left-4 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-gray-200 text-gray-600 hover:bg-gray-300 lg:hidden"
                        aria-label={t('course.closeSidebar')}
                    >
                        <X size={18} />
                    </button>
                    <CourseContentSidebar
                        key={classData.id}
                        course={course}
                        topic={topic}
                        isCompleted={isCompleted}
                        nextTopic={next_topic}
                    />
                </div>
            </div>
        </ClassLayout>
    );
};

const ClassDetail: React.FC<Props> = (props) => {
    return (
        <ThemeWrapper>
            <ClassDetailContent />
        </ThemeWrapper>
    );
};

export default ClassDetail;
